import { Facility } from "../Data/ApiTypes";

interface CachedFacility {
    facilityId: number;
    name: string;
    // Store full facility data but only essential fields are exposed for TopMenu
    fullData: Facility;
    cachedAt: number; // timestamp for cache expiration
}

interface FacilityCacheState {
    facilities: Record<number, CachedFacility>;
}

class FacilityCache {
    private static instance: FacilityCache;
    private cacheState: FacilityCacheState;
    private readonly CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
    private readonly STORAGE_KEY = 'spotSyncFacilityCache';

    private constructor() {
        // Load cache from localStorage or use defaults
        const savedCache = localStorage.getItem(this.STORAGE_KEY);
        this.cacheState = savedCache 
            ? JSON.parse(savedCache)
            : { facilities: {} };
        
        // Clean expired entries on initialization
        this.cleanExpiredEntries();
    }

    public static getInstance(): FacilityCache {
        if (!FacilityCache.instance) {
            FacilityCache.instance = new FacilityCache();
        }
        return FacilityCache.instance;
    }

    private saveState(): void {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cacheState));
    }

    private cleanExpiredEntries(): void {
        const now = Date.now();
        const facilities = this.cacheState.facilities;
        let hasExpired = false;

        Object.keys(facilities).forEach(facilityIdStr => {
            const facilityId = parseInt(facilityIdStr);
            const cached = facilities[facilityId];
            if (cached && (now - cached.cachedAt) > this.CACHE_EXPIRY_MS) {
                delete facilities[facilityId];
                hasExpired = true;
            }
        });

        if (hasExpired) {
            this.saveState();
        }
    }

    public getFacility(facilityId: number): Facility | null {
        this.cleanExpiredEntries();
        
        const cached = this.cacheState.facilities[facilityId];
        if (cached && cached.fullData) {
            return cached.fullData;
        }
        return null;
    }

    public storeFacility(facility: Facility): void {
        this.cacheState.facilities[facility.facilityId] = {
            facilityId: facility.facilityId,
            name: facility.name,
            fullData: facility,
            cachedAt: Date.now()
        };
        this.saveState();
    }

    public clearCache(): void {
        this.cacheState.facilities = {};
        this.saveState();
    }
}

export const facilityCache = FacilityCache.getInstance();
export type { CachedFacility }; 